import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { isSuperAdmin } from '@/utils/adminUtils';
import { auditLogService } from '@/services/auditLogService';

export interface PreviewState {
  organizationId: string;
  organizationName?: string;
  role: string;
  locationIds?: string[];
  enteredAt: Date;
  previewType?: 'role' | 'user'; // Whether previewing by role or by specific user
  previewUserId?: string; // User ID if previewing as specific user
  previewUserName?: string; // User name if previewing as specific user
  previewUserEmail?: string; // User email if previewing as specific user
}

interface PreviewContextType {
  previewState: PreviewState | null;
  isPreviewMode: boolean;
  canUsePreview: boolean;
  enterPreviewMode: (orgId: string, orgName: string, role: string, locationIds?: string[], previewType?: 'role' | 'user', previewUserId?: string, previewUserName?: string, previewUserEmail?: string) => Promise<void>;
  exitPreviewMode: () => Promise<void>;
}

const PreviewContext = createContext<PreviewContextType | undefined>(undefined);

export const PreviewProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [previewState, setPreviewState] = useState<PreviewState | null>(null);
  const [canUsePreview, setCanUsePreview] = useState(false);

  // Check if user can use preview mode (super admin only)
  useEffect(() => {
    if (user?.email) {
      setCanUsePreview(isSuperAdmin(user.email));
    } else {
      setCanUsePreview(false);
    }
  }, [user?.email]);

  // Load preview state from localStorage on mount (persist across page refreshes)
  useEffect(() => {
    if (!canUsePreview) {
      setPreviewState(null);
      return;
    }

    const savedPreview = localStorage.getItem('previewMode');
    if (savedPreview) {
      try {
        const parsed = JSON.parse(savedPreview);
        // Validate that preview state is still valid (not too old)
        const enteredAt = new Date(parsed.enteredAt);
        const hoursSinceEntered = (Date.now() - enteredAt.getTime()) / (1000 * 60 * 60);
        
        // Clear preview if it's been more than 8 hours (safety measure)
        if (hoursSinceEntered < 8) {
          setPreviewState({
            ...parsed,
            enteredAt: enteredAt
          });
        } else {
          localStorage.removeItem('previewMode');
        }
      } catch (error) {
        console.error('Error loading preview state:', error);
        localStorage.removeItem('previewMode');
      }
    }
  }, [canUsePreview]);

  const enterPreviewMode = async (
    orgId: string,
    orgName: string,
    role: string,
    locationIds?: string[],
    previewType: 'role' | 'user' = 'role',
    previewUserId?: string,
    previewUserName?: string,
    previewUserEmail?: string
  ) => {
    if (!canUsePreview || !user) {
      throw new Error('You do not have permission to use preview mode');
    }

    const newPreviewState: PreviewState = {
      organizationId: orgId,
      organizationName: orgName,
      role,
      locationIds: locationIds || [],
      enteredAt: new Date(),
      previewType,
      previewUserId,
      previewUserName,
      previewUserEmail
    };

    setPreviewState(newPreviewState);
    
    // Persist to localStorage
    localStorage.setItem('previewMode', JSON.stringify(newPreviewState));

    // Log to audit trail
    try {
      await auditLogService.log({
        action: 'preview_mode_entered',
        organizationId: orgId,
        performedBy: user.id,
        performedByName: user.name || 'Unknown',
        performedByEmail: user.email || 'Unknown',
        metadata: {
          previewOrganizationId: orgId,
          previewOrganizationName: orgName,
          previewRole: role,
          previewLocationIds: locationIds || [],
          previewType: previewType,
          previewUserId: previewUserId,
          previewUserName: previewUserName,
          previewUserEmail: previewUserEmail
        }
      });
    } catch (error) {
      console.error('Failed to log preview mode entry:', error);
      // Don't fail the preview mode entry if logging fails
    }

    console.log('✅ Entered preview mode:', { orgId, orgName, role });
  };

  const exitPreviewMode = async () => {
    if (!user) return;

    const previousState = previewState;
    setPreviewState(null);
    localStorage.removeItem('previewMode');

    // Log exit to audit trail
    if (previousState) {
      try {
        await auditLogService.log({
          action: 'preview_mode_exited',
          organizationId: previousState.organizationId,
          performedBy: user.id,
          performedByName: user.name || 'Unknown',
          performedByEmail: user.email || 'Unknown',
          metadata: {
            previewOrganizationId: previousState.organizationId,
            previewOrganizationName: previousState.organizationName,
            previewRole: previousState.role,
            previewType: previousState.previewType,
            previewUserId: previousState.previewUserId,
            previewUserName: previousState.previewUserName,
            previewUserEmail: previousState.previewUserEmail
          }
        });
      } catch (error) {
        console.error('Failed to log preview mode exit:', error);
      }
    }

    console.log('✅ Exited preview mode');
  };

  const value: PreviewContextType = {
    previewState,
    isPreviewMode: previewState !== null,
    canUsePreview,
    enterPreviewMode,
    exitPreviewMode
  };

  return <PreviewContext.Provider value={value}>{children}</PreviewContext.Provider>;
};

export const usePreview = () => {
  const context = useContext(PreviewContext);
  if (context === undefined) {
    throw new Error('usePreview must be used within a PreviewProvider');
  }
  return context;
};

